/**
 * T-0894 (`docs/concept-mobile-app.md`, LC-6g, its third part): a desktop failure's reference (DG-9)
 * and the error a contained panic becomes, without the notice's state. A browser's entry asks
 * `desktopFailureReference` when it words a failure (`actionFailure.ts`, `graphqlRequestError.ts`);
 * the rest of `desktopCommandFailure.ts` is the desktop's own and stays out of that entry. It imports
 * nothing.
 */

/** The `kind` `command_boundary.rs` gives a contained panic (`COMMAND_PANICKED`). */
export const DESKTOP_COMMAND_PANICKED = 'commandPanicked';

export interface IDesktopCommandFailure {
  kind: typeof DESKTOP_COMMAND_PANICKED;
  /** The command, as the page invoked it (`cloud_api_request`). */
  command: string;
  message: string;
  location: string | null;
  /** F-0689 DG-9: the reference its line in the app's log holds, as `Request id …` shows it. */
  requestId?: string | null;
}

/** How many failure sentences keep their reference: the recent ones a screen can still be showing. */
const REFERENCES_KEPT = 50;
const references = new Map<string, string>();

export function rememberDesktopFailureReference(message: string, requestId: string): void {
  references.delete(message);
  references.set(message, requestId);
  while (references.size > REFERENCES_KEPT) {
    const oldest = references.keys().next().value;
    if (oldest === undefined) break;
    references.delete(oldest);
  }
}

/**
 * The reference of a desktop failure the page caught (DG-9): the latest one the app logged for that
 * sentence, or a contained panic's own. `null` for anything else.
 */
export function desktopFailureReference(error: unknown): string | null {
  if (typeof error === 'string') return references.get(error) ?? null;
  if (error instanceof DesktopCommandFailedError) return error.failure.requestId ?? null;
  return null;
}

/** What a caller of a desktop command catches when the command panicked. */
export class DesktopCommandFailedError extends Error {
  readonly failure: IDesktopCommandFailure;

  constructor(failure: IDesktopCommandFailure) {
    super(
      `The desktop app could not finish "${describeDesktopCommand(failure.command)}". FEATit is still running; try again.`,
    );
    this.name = 'DesktopCommandFailedError';
    this.failure = failure;
  }
}

/** `cloud_api_request` → `cloud api request`: the command, readable, for a person. */
export function describeDesktopCommand(command: string): string {
  return command.replace(/_/g, ' ').trim();
}
